import { LOG_SUMMARY_OPTION } from 'constants/general';
import { LogQuantityQuestion } from 'interfaces';
import { ActivityType, ChoiceType } from 'interfaces/common.interface';

/** Random activities store log config on choices[], not the parent (BE contract). */
export const activityHasPerChoiceLogging = (activity: ActivityType): boolean =>
  (activity.choices?.length ?? 0) > 0;

const normalizeActivityParentLogForChoices = (
  activity: ActivityType
): ActivityType => {
  if (!activityHasPerChoiceLogging(activity)) {
    return activity;
  }
  return {
    ...activity,
    log_quantity: false,
    log_quantity_questions: [],
    log_summary_type: LOG_SUMMARY_OPTION.SUMMATION
  };
};

const denormalizeActivityParentLogForChoices = (
  activity: ActivityType
): ActivityType => {
  if (!activityHasPerChoiceLogging(activity)) {
    return activity;
  }
  return {
    ...activity,
    log_quantity: false,
    log_quantity_questions: [],
    log_summary_type: LOG_SUMMARY_OPTION.SUMMATION
  };
};

export const isLogQuantityBoundProvided = (
  value: number | undefined
): value is number =>
  value !== undefined && Number.isFinite(value);

/** API uses 0/0 for “no slider bounds”; in-app we treat that as unset optional fields. */
export const areLogQuantityBoundsUnset = (
  question: LogQuantityQuestion
): boolean => {
  if (question.min_value === 0 && question.max_value === 0) {
    return true;
  }
  return (
    !isLogQuantityBoundProvided(question.min_value) &&
    !isLogQuantityBoundProvided(question.max_value)
  );
};

const normalizeOptionalDescription = (
  value: string | null | undefined
): string | undefined => (value == null || value === '' ? undefined : value);

export const normalizeLogQuantityQuestion = (
  question: LogQuantityQuestion
): LogQuantityQuestion => {
  const withDescriptions: LogQuantityQuestion = {
    ...question,
    min_value_description: normalizeOptionalDescription(
      question.min_value_description
    ),
    max_value_description: normalizeOptionalDescription(
      question.max_value_description
    )
  };

  if (!areLogQuantityBoundsUnset(withDescriptions)) {
    return withDescriptions;
  }
  const { min_value: _min, max_value: _max, ...rest } = withDescriptions;
  return rest;
};

export const normalizeLogQuantityQuestions = (
  questions: LogQuantityQuestion[] | undefined
): LogQuantityQuestion[] | undefined => {
  if (!questions?.length) {
    return questions;
  }
  return questions.map(normalizeLogQuantityQuestion);
};

export const denormalizeLogQuantityQuestionForApi = (
  question: LogQuantityQuestion
): LogQuantityQuestion => {
  if (!areLogQuantityBoundsUnset(question)) {
    return question;
  }
  return { ...question, min_value: 0, max_value: 0 };
};

export const denormalizeLogQuantityQuestionsForApi = (
  questions: LogQuantityQuestion[] | undefined
): LogQuantityQuestion[] | undefined => {
  if (!questions?.length) {
    return questions;
  }
  return questions.map(denormalizeLogQuantityQuestionForApi);
};

export const normalizeActivityLogQuantityQuestions = (
  activity: ActivityType
): ActivityType =>
  normalizeActivityParentLogForChoices({
    ...activity,
    log_quantity_questions: normalizeLogQuantityQuestions(
      activity.log_quantity_questions
    ),
    choices: activity.choices?.map((choice: ChoiceType) => ({
      ...choice,
      log_quantity_questions: normalizeLogQuantityQuestions(
        choice.log_quantity_questions
      )
    }))
  });

export const denormalizeActivityLogQuantityQuestionsForApi = (
  activity: ActivityType
): ActivityType =>
  denormalizeActivityParentLogForChoices({
    ...activity,
    log_quantity_questions: denormalizeLogQuantityQuestionsForApi(
      activity.log_quantity_questions
    ),
    choices: activity.choices?.map((choice: ChoiceType) => ({
      ...choice,
      log_quantity_questions: denormalizeLogQuantityQuestionsForApi(
        choice.log_quantity_questions
      )
    }))
  });

export const createDefaultLogQuantityQuestion = (
  id: string
): LogQuantityQuestion => ({
  id,
  question: ''
});

export const isLogQuantityMinRequired = (
  question: LogQuantityQuestion
): boolean =>
  isLogQuantityBoundProvided(question.max_value) &&
  !isLogQuantityBoundProvided(question.min_value);

export const isLogQuantityMaxRequired = (
  question: LogQuantityQuestion
): boolean =>
  isLogQuantityBoundProvided(question.min_value) &&
  !isLogQuantityBoundProvided(question.max_value);

/** Both empty is valid; both set requires min < max; only one set is invalid. */
export const isLogQuantityQuestionRangeValid = (
  question: LogQuantityQuestion
): boolean => {
  const minProvided = isLogQuantityBoundProvided(question.min_value);
  const maxProvided = isLogQuantityBoundProvided(question.max_value);

  if (!minProvided && !maxProvided) {
    return true;
  }

  if (minProvided !== maxProvided) {
    return false;
  }

  const { min_value: min, max_value: max } = question;
  if (!isLogQuantityBoundProvided(min) || !isLogQuantityBoundProvided(max)) {
    return false;
  }

  return min < max;
};

export const hasLogQuantityRangeError = (
  question: LogQuantityQuestion
): boolean => {
  const min = question.min_value;
  const max = question.max_value;
  if (!isLogQuantityBoundProvided(min) || !isLogQuantityBoundProvided(max)) {
    return false;
  }

  return min >= max;
};

export const getLogQuantityMinErrorKey = (
  question: LogQuantityQuestion
): string | undefined => {
  if (isLogQuantityMinRequired(question)) {
    return 'tabs_revamp.log_data_tab.min_required_when_max_set';
  }
  if (hasLogQuantityRangeError(question)) {
    return 'min_should_be_less_than_max';
  }
  return undefined;
};

export const getLogQuantityMaxErrorKey = (
  question: LogQuantityQuestion
): string | undefined => {
  if (isLogQuantityMaxRequired(question)) {
    return 'tabs_revamp.log_data_tab.max_required_when_min_set';
  }
  if (hasLogQuantityRangeError(question)) {
    return 'min_should_be_less_than_max';
  }
  return undefined;
};

export const isLogQuantityQuestionValid = (
  question: LogQuantityQuestion
): boolean =>
  Boolean(question.question?.trim()) &&
  isLogQuantityQuestionRangeValid(question);

/** When data logging is on and questions exist, every question must be valid. */
export const areLogQuantityQuestionsValid = (
  questions: LogQuantityQuestion[] | undefined,
  logQuantityEnabled?: boolean
): boolean => {
  if (!logQuantityEnabled) {
    return true;
  }
  const list = questions ?? [];
  if (!list.length) {
    return true;
  }
  return list.every(isLogQuantityQuestionValid);
};

export const formatLogQuantityBoundValue = (
  value: number | undefined
): string =>
  isLogQuantityBoundProvided(value) ? String(value) : '';

export const parseLogQuantityBoundValue = (
  value: string
): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};
